import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Button, Spinner } from 'react-bootstrap';
import { authService } from '../services/api';

const VerifyEmail = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('idle'); // idle, sending, success, error
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    const verifyEmailToken = async () => {
      try {
        if (token) {
          const response = await authService.verifyEmail(token);
          setStatus('success');
          setMessage(response.msg || 'Votre adresse email a été vérifiée avec succès.');
          
          // Rediriger vers la page de connexion après 3 secondes
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setStatus('error');
          setMessage('Token de vérification manquant.');
        }
      } catch (error) {
        setStatus('error');
        setMessage(error.msg || 'Erreur lors de la vérification de l\'email. Le token est peut-être invalide ou expiré.');
      }
    };

    verifyEmailToken();
  }, [token, navigate]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setResendStatus('error');
      setResendMessage('Veuillez entrer votre adresse email.');
      return;
    }

    try {
      setResendStatus('sending');
      const response = await authService.resendVerificationEmail(email);
      setResendStatus('success');
      setResendMessage(response.msg || 'Un nouvel email de vérification a été envoyé. Veuillez vérifier votre boîte de réception.');
    } catch (error) {
      setResendStatus('error');
      setResendMessage(error.msg || 'Erreur lors de l\'envoi de l\'email de vérification.');
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <h4 className="mb-0">Vérification de l'adresse email</h4>
            </Card.Header>
            <Card.Body>
              {status === 'loading' && (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3">Vérification de votre adresse email en cours...</p>
                </div>
              )}

              {status === 'success' && (
                <Alert variant="success">
                  <Alert.Heading>Vérification réussie!</Alert.Heading>
                  <p>{message}</p>
                  <p>Vous allez être redirigé vers la page de connexion dans quelques secondes...</p>
                  <hr />
                  <div className="d-flex justify-content-end">
                    <Button variant="outline-success" as={Link} to="/login">
                      Aller à la page de connexion
                    </Button>
                  </div>
                </Alert>
              )}

              {status === 'error' && (
                <>
                  <Alert variant="danger">
                    <Alert.Heading>Erreur de vérification</Alert.Heading>
                    <p>{message}</p>
                  </Alert>

                  <div className="mt-4">
                    <h5>Renvoyer un email de vérification</h5>
                    <p>Si vous n'avez pas reçu l'email de vérification ou si le lien a expiré, vous pouvez demander un nouvel email :</p>
                    
                    <form onSubmit={handleResendVerification}>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">Adresse email</label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Entrez votre adresse email"
                          required
                        />
                      </div>
                      
                      <Button 
                        type="submit" 
                        variant="primary" 
                        disabled={resendStatus === 'sending'}
                        className="w-100"
                      >
                        {resendStatus === 'sending' ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className="ms-2">Envoi en cours...</span>
                          </>
                        ) : 'Renvoyer l\'email de vérification'}
                      </Button>
                    </form>
                    
                    {resendStatus === 'success' && (
                      <Alert variant="success" className="mt-3">
                        {resendMessage}
                      </Alert>
                    )}
                    
                    {resendStatus === 'error' && (
                      <Alert variant="danger" className="mt-3">
                        {resendMessage}
                      </Alert>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
            <Card.Footer className="text-center">
              <div className="d-flex justify-content-between">
                <Button variant="link" as={Link} to="/login" className="text-decoration-none">
                  Connexion
                </Button>
                <Button variant="link" as={Link} to="/register" className="text-decoration-none">
                  Inscription
                </Button>
                <Button variant="link" as={Link} to="/" className="text-decoration-none">
                  Accueil
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VerifyEmail;
